import { Table, TableBody, TableContainer, TableHead, TableRow, Switch, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box, TableCell } from '@mui/material';
import React, { useState } from 'react';
import ConfirmModal from '../common/modal/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useSelector, useDispatch } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../utils/methods';
import { updatePaymentPermission } from '../../api/store-management';
import { toggleBranchExposure, updateBranchOperationType } from '../../api/exposure';
import { useTranslation } from 'react-i18next';
import { setUserData } from '../../actions/user';
import { getHeadBranchId } from '../../api/point';
import useUserAuth from '../../utils/useUserAuth';

const StyledHeaderCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: '13px',
  color: '#333',
  padding: '10px 8px',
  borderRight: '1px solid #ddd',
  '&:last-child': { borderRight: 'none' },
});

const StyledBodyRow = styled(TableRow)({
  height: '44px',
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#f8f9fa' },
  borderBottom: '1px solid #eee',
});

const StyledBodyCell = styled(TableCell)({
  fontSize: '13px',
  color: '#333',
  padding: '8px',
  borderRight: '1px solid #f0f0f0',
  '&:last-child': { borderRight: 'none' },
});

const TypeBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'type',
})(({ type }) => ({
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  ...(type === 'HEAD'
    ? { color: '#0F7F85', backgroundColor: '#e6f5f5' }
    : { color: '#5A5A5A', backgroundColor: '#f0f0f0' }),
}));

// 지점유형(직영/가맹/기타) 드롭박스의 색상 톤. 옆 계정유형 배지와 나란히 있어도 어색하지 않도록
// 같은 크기·모양(pill)에 값별 색만 다르게 준다.
const OPERATION_TYPE_COLORS = {
  DIRECT: { color: '#0F7F85', backgroundColor: '#e6f5f5' },
  FRANCHISE: { color: '#5A5A5A', backgroundColor: '#f0f0f0' },
  ETC: { color: '#9A6400', backgroundColor: '#FDF1DC' },
};

// 본점은 지점유형이 직영으로 고정이라 드롭박스 대신 같은 톤의 pill 만 보여준다.
const FixedOperationBadge = styled('span')({
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  ...OPERATION_TYPE_COLORS.DIRECT,
});

const OperationTypeSelect = styled(Select, {
  shouldForwardProp: prop => prop !== 'opType',
})(({ opType }) => {
  const tone = OPERATION_TYPE_COLORS[opType] || OPERATION_TYPE_COLORS.FRANCHISE;
  return {
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    color: tone.color,
    backgroundColor: tone.backgroundColor,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-select': {
      padding: '3px 22px 3px 10px !important',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSelect-icon': {
      color: tone.color,
      right: '4px',
    },
  };
});

export default function StoreTable({ centersList, getCentersList, setCentersList, onBeforeNavigate }) {
  const { setCentersId } = useApp();
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canViewEdit = useUserAuth('CENTER_VIEW_EDIT');
  const [exposureConfirmOpen, setExposureConfirmOpen] = useState(false);
  const [pendingExposureCenter, setPendingExposureCenter] = useState(null);

  const changePaymentStatus = async (e, id) => {
    try {
      await updatePaymentPermission(user.token, id);
      toast.success(t('Center status changed'));
      await getCentersList();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const navigateToDetail = async center => {
    // SUPERADMIN인 경우 Header의 지점 선택에도 반영
    if (user?.userRole === 'ROLE_SUPERADMIN') {
      try {
        // headBranch 데이터 구성
        const headBranch = {
          headBranchId: center.centerId,
          headBranchName: center.branchName,
        };

        // 해당 센터의 지점 목록 조회
        const branchList = await getHeadBranchId(user.token, center.centerId);
        const firstBranch = branchList && branchList.length > 0 ? branchList[0] : null;

        // Redux에 headBranch와 branch 설정
        dispatch(
          setUserData({
            ...user,
            headBranch: headBranch,
            branch: firstBranch,
          }),
        );
      } catch (error) {
        console.error('지점 목록 조회 실패:', error);
        // 오류가 발생해도 headBranch는 설정
        dispatch(
          setUserData({
            ...user,
            headBranch: {
              headBranchId: center.centerId,
              headBranchName: center.branchName,
            },
            branch: null,
          }),
        );
      }
    }

    onBeforeNavigate?.();
    navigate(`/store/${center.centerId}`);
  };

  function formatPhoneNumber(phoneNumber) {
    // Check if the phone number already has hyphens
    if (phoneNumber.includes('-')) {
      // If hyphens are already there, just return the number as it is
      return phoneNumber;
    }

    // Remove any non-numeric characters (to prepare for formatting)
    phoneNumber = phoneNumber.replace(/\D/g, '');

    // If the number has 10 digits (landline), format it as xx-xxxx-xxxx
    if (phoneNumber.length === 10) {
      return phoneNumber.replace(/^(\d{2})(\d{4})(\d{4})$/, '$1-$2-$3');
    }
    // If the number has 11 digits (mobile), format it as xxx-xxxx-xxxx
    else if (phoneNumber.length === 11) {
      return phoneNumber.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1-$2-$3');
    }

    // If the number is not in the expected format, return it as is
    return phoneNumber;
  }

  return (
    <>
    <TableContainer sx={{ border: '1px solid #e8e8e8', borderRadius: '8px', marginTop: '10px' }}>
      <Table size="small">
        <TableHead sx={{ background: '#F0F2F5' }}>
          <TableRow sx={{ height: '44px' }}>
            <StyledHeaderCell align="center">{t('Number')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Branch Name')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Representative number')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Representative name')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t("Representative's mobile phone")}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Representative Email')}</StyledHeaderCell>
            <StyledHeaderCell align="center">계정유형</StyledHeaderCell>
            <StyledHeaderCell align="center">지점유형</StyledHeaderCell>
            <StyledHeaderCell align="center">앱 노출</StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(!centersList || centersList.length === 0) && (
            <TableRow>
              <StyledBodyCell colSpan={9} align="center" sx={{ padding: '60px 0', color: '#999', fontSize: '14px', borderRight: 'none' }}>
                검색 결과가 없습니다.
              </StyledBodyCell>
            </TableRow>
          )}
          {centersList &&
            centersList.map(center => (
              <StyledBodyRow
                key={center.centerId}
                style={{ cursor: canViewEdit ? 'pointer' : 'default' }}>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center.centerId || '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center.branchName || '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center?.representativeNumber ? formatPhoneNumber(center.representativeNumber) : '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center.representativeName || '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center.representativePhoneNumber ? formatPhoneNumber(center.representativePhoneNumber) : '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center.representativeEmail || '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center" onClick={() => canViewEdit && navigateToDetail(center)}>
                  {center.type ? <TypeBadge type={center.type}>{center.type === 'HEAD' ? '본점' : '지점'}</TypeBadge> : '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center">
                  {/* 본점(HEAD)의 매장은 언제나 직영이라 드롭박스 없이 '직영' 고정으로 보여준다.
                      서버도 본점 지점의 운영형태를 DIRECT 로 고정해 다지점 공유 상품(#73) 후보에 항상 포함시킨다. */}
                  {!center.type || !center.branchId ? (
                    '-'
                  ) : center.type === 'HEAD' ? (
                    <div onClick={() => canViewEdit && navigateToDetail(center)} style={{ cursor: canViewEdit ? 'pointer' : 'default' }}>
                      <FixedOperationBadge>직영</FixedOperationBadge>
                    </div>
                  ) : (
                    <div onClick={e => e.stopPropagation()}>
                      <OperationTypeSelect
                        size="small"
                        opType={center.operationType || 'FRANCHISE'}
                        value={center.operationType || 'FRANCHISE'}
                        onChange={async e => {
                          e.stopPropagation();
                          const newValue = e.target.value;
                          try {
                            await updateBranchOperationType(user.token, center.branchId, newValue);
                            setCentersList(prev => prev.map(c => c.centerId === center.centerId ? { ...c, operationType: newValue } : c));
                            toast.success('지점유형이 변경되었습니다');
                          } catch { toast.error('변경에 실패했습니다'); }
                        }}>
                        <MenuItem value="DIRECT">직영</MenuItem>
                        <MenuItem value="FRANCHISE">가맹</MenuItem>
                        <MenuItem value="ETC">기타</MenuItem>
                      </OperationTypeSelect>
                    </div>
                  )}
                </StyledBodyCell>
                <StyledBodyCell align="center">
                  <div onClick={e => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={center.exposureYn === 'Y'}
                    onChange={async e => {
                      e.stopPropagation();
                      if (!center.branchId) {
                        setPendingExposureCenter(center);
                        setExposureConfirmOpen(true);
                        return;
                      }
                      const newValue = center.exposureYn === 'Y' ? 'N' : 'Y';
                      try {
                        await toggleBranchExposure(user.token, center.branchId, newValue);
                        setCentersList(prev => prev.map(c => c.centerId === center.centerId ? { ...c, exposureYn: newValue } : c));
                        toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨겨집니다');
                      } catch { toast.error('변경에 실패했습니다'); }
                    }}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' } }}
                  />
                  </div>
                </StyledBodyCell>
                {/* Allow checkbox to function independently */}
                {/*<TableCell align="center">
                  <FormControlLabel
                    sx={{ ml: '10px' }}
                    onClick={e => e.stopPropagation()} // Prevent row click from triggering when interacting with the switch
                    control={
                      <IOSSwitch
                        sx={{
                          '.MuiSwitch-track': {
                            background: center.paymentPermission ? '#0F7F85 !important' : '#ccc !important',
                          },
                        }}
                        checked={center.paymentPermission}
                        onChange={e => changePaymentStatus(e, center.centerManagerId)} // Move onChange directly to the switch
                      />
                    }
                    disabled={user.userRole !== 'ROLE_SUPERADMIN'}
                  />
                </TableCell>*/}
              </StyledBodyRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>

    <ConfirmModal
      open={exposureConfirmOpen}
      onClose={() => { setExposureConfirmOpen(false); setPendingExposureCenter(null); }}
      onConfirm={() => { setExposureConfirmOpen(false); setPendingExposureCenter(null); }}
      message={`아직 매장 정보가 등록되지 않았습니다.\n매장 정보를 등록해주세요.`}
      highlightText="__none__"
      highlightColor="#4AB3BC"
      cancelText="닫기"
      confirmText="확인"
      confirmColor="#4AB3BC"
    />
    </>
  );
}
